import { supabase } from '../client'
import type { CloudHabitLog } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── HABIT LOGS (Cloud) ─────────────────────────────────────────── */

export async function getCloudHabitLogs(userId: string, since?: Date): Promise<CloudHabitLog[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('habit_logs')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('date', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get habit_logs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get habit_logs error', error)
    return []
  }
}

export async function getCloudHabitLogIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('habit_logs')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get habit_log IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get habit_log IDs error', error)
    return []
  }
}

export async function getCloudHabitLogsByIds(userId: string, ids: string[]): Promise<CloudHabitLog[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('habit_logs')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get habit_logs by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get habit_logs by IDs error', error)
    return []
  }
}

export async function getDeletedCloudHabitLogs(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('habit_logs')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted habit_logs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted habit_logs error', error)
    return []
  }
}

/**
 * Create (or upsert) a habit log. Upserts on the (user_id, habit_id, date)
 * unique key so two devices logging the same day converge instead of erroring.
 */
export async function createCloudHabitLog(
  userId: string,
  log: Omit<CloudHabitLog, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudHabitLog | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('habit_logs')
      .upsert({ ...log, user_id: userId }, { onConflict: 'user_id,habit_id,date' })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create habit_log error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create habit_log error', error)
    return null
  }
}

export async function updateCloudHabitLog(
  logId: string,
  updates: Partial<Omit<CloudHabitLog, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('habit_logs')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', logId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update habit_log error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update habit_log error', error)
    return null
  }
}

export async function deleteCloudHabitLog(logId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('habit_logs')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', logId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete habit_log error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete habit_log error', error)
    return false
  }
}
